/**
 * useSecureAuth (compatibility wrapper)
 *
 * This hook is kept for backward compatibility while consumers migrate to
 * useSecureAuthContext. It intentionally delegates all state ownership to
 * SecureAuthProvider to avoid dual auth state machines.
 */

import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import type { LoginCredentials, AuthResult } from '../services/SecureAuthService';
import { useSecureAuthContext } from '../providers/SecureAuthProvider';

type SecureAuthTokenStatus = ReturnType<typeof useSecureAuthContext>['tokenStatus'];

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthResult['user'] | null;
  tokenStatus: SecureAuthTokenStatus;
}

interface UseSecureAuthReturn extends AuthState {
  login: (credentials: LoginCredentials) => Promise<AuthResult>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

export function useSecureAuth(): UseSecureAuthReturn {
  const {
    isAuthenticated,
    isLoading,
    user,
    tokenStatus,
    login,
    logout,
    refreshAuth,
  } = useSecureAuthContext();

  const refreshToken = useCallback(async () => {
    await refreshAuth();
  }, [refreshAuth]);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    return isAuthenticated;
  }, [isAuthenticated]);

  return {
    isAuthenticated,
    isLoading,
    user,
    tokenStatus,
    login,
    logout,
    refreshToken,
    checkAuth,
  };
}

// Export helper hook for requiring authentication
export function useRequireAuth(redirectTo: string = '/login') {
  const { isAuthenticated, isLoading } = useSecureAuth();
  const router = useRouter();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
      router.push(`${redirectTo}?returnUrl=${returnUrl}`);
    }
  }, [isAuthenticated, isLoading, router, redirectTo]);

  return { isAuthenticated, isLoading };
}